import React, {Fragment, useEffect, useState} from "react";
import {
  Toggle,
  InputGroup,
  Button,
  toaster,
  Message,
  Input,
  Notification,
  ButtonGroup,
  IconButton,
  Whisper, RadioTileGroup, RadioTile
} from "rsuite";
import {extractTimezoneFromISO, prepareTimeData} from "../Dimmer/utils/Dimmer.utils";
import RebootConfirmationModal from "../Dimmer/components/RebootConfirmationModal";
import MechanismApi from "../../../../../services/Mechanism.api";
import {SharedApi} from "../../../../../services/Shared.api";
import './MechanismComponent.scss'
import MechanismPrivateDescriptionSection from "./components/MechanismPrivateDescriptionSection";
import MechanismMeteoDataSection from "./components/MechanismMeteoDataSection";
import AltitudeCalibrationInfo from "./components/AltitudeCalibrationInfo";
import HandsCorrection from "./components/HandsCorrection";
import {getTooltip} from '../../../../../shared/Tooltip';
import {FaAngleDoubleLeft, FaAngleDoubleRight, FaStop} from "react-icons/fa";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import MechanismInfoSection from "./components/MechanismInfoSection";
import MechanismDataSection from "./components/MechanismDataSection";
import {formatAdjustmentTime, formatStopTimer, parseStopTimer} from "./utils/Mechaism.utils";
import {formatTimezoneOffset} from "../../../../../shared/Time.utils";
import DeviceEvents from "../../../../shared/DeviceEvents/DeviceEvents";
import {TfiSaveIcon} from "../../../../../models/Icons.model";

const OFFLINE_SECTION_STYLE = {
  opacity: 0.6,
  pointerEvents: 'none'
};

const MechanismComponent = ({mechanismId, deviceType = '', isAdmin, isConnected = true}) => {
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [currentAltitude, setCurrentAltitude] = useState('');
  const [savedAltitude, setSavedAltitude] = useState('');
  const [calibrationRefreshKey, setCalibrationRefreshKey] = useState(0);
  const [movementPermission, setMovementPermission] = useState(false);
  const [autonomousEnabled, setAutonomousEnabled] = useState(false);
  const [hourHandPosition, setHourHandPosition] = useState(0);
  const [minuteHandPosition, setMinuteHandPosition] = useState(0);
  const [deviceTime, setDeviceTime] = useState(null);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const [userTimezone, setUserTimezone] = useState('+3');
  const [meteoData, setMeteoData] = useState({temperature: 0, pressure: 0, humidity: 0});
  const [impulse, setImpulse] = useState(0);
  const [movement, setMovement] = useState('');
  const [adjustmentEnabled, setAdjustmentEnabled] = useState('');
  const [adjustmentTime, setAdjustmentTime] = useState(0);
  const [stopTimer, setStopTimer] = useState('');
  const [isStopTimerRunning, setIsStopTimerRunning] = useState(false);
  const [mechanism, setMechanism] = useState(null);
  const [isDaylightSavingEnabled, setIsDaylightSavingEnabled] = useState(false);

  const resetLiveState = () => {
    setCurrentAltitude('');
    setSavedAltitude('');
    setMovementPermission(false);
    setAutonomousEnabled(false);
    setHourHandPosition(0);
    setMinuteHandPosition(0);
    setDeviceTime(null);
    setUserTimezone('+3');
    setMeteoData({temperature: 0, pressure: 0, humidity: 0});
    setImpulse(0);
    setMovement('');
    setAdjustmentEnabled('');
    setAdjustmentTime(0);
    setStopTimer('');
    setIsStopTimerRunning(false);
    setMechanism(null);
    setIsDaylightSavingEnabled(false);
  };

  useEffect(() => {
    if (mechanismId !== null && mechanismId !== undefined) {
      resetLiveState();
      fetchAndSetData().then();

      if (isConnected) {
        getTime().then();
        initialLoad().then();
      }
    }
  }, [mechanismId, isConnected]);

  const successRebootMessage = (
    <Notification type={"success"} closable>
      <p>Устройство перезапускается</p>
    </Notification>
  );

  const errorRebootMessage = (
    <Notification type={"error"} closable>
      <p>Ошибка при перезапуске устройства</p>
    </Notification>
  );

  useEffect(() => {
    checkMechanismMovement()
  }, [movementPermission]);

  useEffect(() => {
    if (!isStopTimerRunning || !isConnected) return undefined;

    const seconds = parseStopTimer(stopTimer);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      setIsStopTimerRunning(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setStopTimer(formatStopTimer(seconds - 1));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [isStopTimerRunning, isConnected, stopTimer]);

  const handleOpenRebootModal = () => {
    setShowRebootModal(true);
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleConfirmReboot = async () => {
    setLoading(true);
    setShowRebootModal(false);

    try {
      const response = await SharedApi.rebootDevice(mechanismId)

      if (response) {
        toaster.push(successRebootMessage, {placement: "topEnd"});
      }
    } catch (error) {
      toaster.push(errorRebootMessage, {placement: "topEnd"});
      console.error("Ошибка при перезапуске устройства:", error);
    }
  };

  const successMessage = (
    <Notification type={"success"} closable>
      <p>Настройки сохранены</p>
    </Notification>
  );

  const errorMessage = (
    <Notification type={"error"} closable>
      <p>Ошибка</p>
    </Notification>
  );

  // Текст ошибки от API (DomainException: {message}), иначе общее «Ошибка»
  const apiErrorMessage = (error) => {
    const text = error?.body?.message || (typeof error?.body === "string" ? error.body : null);
    return (
      <Notification type={"error"} closable>
        <p>{text || "Ошибка"}</p>
      </Notification>
    );
  };

  const getTime = async () => {
    setLoading(true);

    try {
      const data = await MechanismApi.getTime(mechanismId);
      setIsDaylightSavingEnabled(Boolean(data?.isDaylightSavingEnabled));

      if (data.time) {
        const timezone = extractTimezoneFromISO(data.time);
        setUserTimezone(timezone);

        const match = data.time.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [, year, month, day, hours, minutes, seconds] = match;

          const date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
          );

          setDeviceTime(date);
        } else {
          setDeviceTime(new Date(data.time));
        }
      }

    } catch (e) {
      console.error('Ошибка получения данных:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const updateDaylightSaving = async (enabled) => {
    setLoading(true);
    try {
      const ok = await SharedApi.updateDaylightSaving(mechanismId, enabled);

      if (ok) {
        setIsDaylightSavingEnabled(enabled);
        toaster.push(successMessage, {placement: "topEnd"});
      } else {
        toaster.push(errorMessage, {placement: "topEnd"});
      }
    } catch (e) {
      console.error("Ошибка при обновлении настроек:", e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDaylightSaving = async (enabled) => {
    await updateDaylightSaving(enabled);
  };

  const initialLoad = async () => {
    try {
      await loadMechanismInfoUpdate();
      await getMeteoData();
      await getImpulse();

    } catch (error) {
      console.error(error);
    }
  }

  const disableFindButton = () => {
    if (movement === 'Backward' || movement === 'Forward') {
      return true;
    }
    return adjustmentTime !== 0;
  }

  const fetchAndSetData = async () => {
    try {
      const result = await SharedApi.getDeviceInfo(String(mechanismId));
      setDeviceData(result);
    } catch (e) {
      toaster.push(
        <Message type="error" closable>
          Ошибка при загрузке данных о механизме
        </Message>,
        {placement: 'topEnd'}
      )
    } finally {
      setLoading(false);
    }
  }

  const loadMechanismInfoUpdate = async () => {
    setLoading(true);
    try {
      const result = await MechanismApi.getMechanismParams(mechanismId);

      setMechanism(result);
      setHourHandPosition(result.arrowPosition.hour)
      setMinuteHandPosition(result.arrowPosition.minute)
      setMovementPermission(result.movementPermission)
      setAutonomousEnabled(result.autoMode)
      setAdjustmentEnabled(result?.adjustmentEnabled)
      setMovement(result?.adjustmentMode)
      setAdjustmentTime(result?.adjustmentTime)
      setStopTimer(formatStopTimer(result?.stopTimer))
      setIsStopTimerRunning(Number(result?.stopTimer) > 0)
      setCurrentAltitude(result?.currentAltitude ?? '')
      setSavedAltitude(result?.savedAltitude ?? '')

    } catch (error) {
      toaster.push(<Message type="error" closable>{error.message}</Message>, {duration: 3000});
    } finally {
      setLoading(false);
    }
  };

  const getMeteoData = async () => {
    setLoading(true);
    try {
      const result = await MechanismApi.getMechanismMeteoData(mechanismId);
      setMeteoData(result);

    } catch (error) {
      toaster.push(<Message type="error" closable>{error.message}</Message>, {duration: 3000});
    } finally {
      setLoading(false);
    }
  }

  const getImpulse = async () => {
    setLoading(true);
    try {
      const result = await MechanismApi.getMechanismImpulse(mechanismId);
      setImpulse(result?.impulse);

    } catch (error) {
      toaster.push(<Message type="error" closable>{error.message}</Message>, {duration: 3000});
    } finally {
      setLoading(false);
    }
  }

  const changeMovementPermission = async (state) => {
    setLoading(true);
    try {
      const result = await MechanismApi.setMovementPermission(mechanismId, state);
      if (result) {
        loadMechanismInfoUpdate().then()
        toaster.push(successMessage, {placement: "topEnd"});
      }
    } catch (error) {
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const changeAutoMode = async (state) => {
    setLoading(true);
    try {
      const result = await MechanismApi.setAutoMode(mechanismId, state);

      if (result) {
        loadMechanismInfoUpdate().then()
        toaster.push(successMessage, {placement: "topEnd"});
      }
    } catch (error) {
      console.error(error)
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const changeArrowsPosition = async (nextHourPosition, nextMinutePosition) => {
    const hoursPosition = nextHourPosition ?? hourHandPosition;
    const minutesPosition = nextMinutePosition ?? minuteHandPosition;

    setLoading(true);
    try {
      const result = await MechanismApi.setArrowsPosition(mechanismId, hoursPosition, minutesPosition);
      if (result) {
        loadMechanismInfoUpdate().then()
        toaster.push(successMessage, {placement: "topEnd"});
      }
    } catch (e) {
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSaveTime = async () => {
    setLoading(true);
    try {
      const timeData = prepareTimeData(deviceTime, userTimezone);
      await MechanismApi.saveTime(mechanismId, timeData);
      toaster.push(successMessage, {placement: "topEnd"});
      await getTime();
    } catch (e) {
      console.error('Ошибка при сохранении времени:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSaveTimezone = async () => {
    setLoading(true);
    try {
      const formattedTimezone = formatTimezoneOffset(userTimezone);
      await SharedApi.updateTimezone(mechanismId, formattedTimezone);
      toaster.push(successMessage, {placement: "topEnd"});
      await getTime();
    } catch (e) {
      console.error('Ошибка при сохранении времени:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSaveUTCTime = async (newDate) => {
    setLoading(true);
    try {
      await SharedApi.updateUTCTime(mechanismId, newDate);
      toaster.push(successMessage, {placement: "topEnd"});
      await getTime();
    } catch (e) {
      console.error('Ошибка при сохранении времени:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSavePlace = async () => {
    setLoading(true);
    try {
      await SharedApi.updatePlace(mechanismId, deviceData?.place);
      toaster.push(successMessage, {placement: "topEnd"});
      await fetchAndSetData();
    } catch (e) {
      console.error('Ошибка при сохранении места:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const onSaveArticle = async () => {
    setLoading(true);
    try {
      await SharedApi.updateArticle(mechanismId, deviceData?.article);
      toaster.push(successMessage, {placement: "topEnd"});
      await fetchAndSetData();
    } catch (e) {
      console.error('Ошибка при сохранении артикула:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const onSaveModel = async () => {
    setLoading(true);
    try {
      await SharedApi.updateModel(mechanismId, deviceData?.model);
      toaster.push(successMessage, {placement: "topEnd"});
      await fetchAndSetData();
    } catch (e) {
      console.error('Ошибка при сохранении модели:', e);
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }


  const handleTimezoneChange = (value) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const onChangePlace = (value) => {
    setDeviceData(prev => ({
      ...prev,
      place: value
    }));
  }

  const onChangeArticle = (value) => {
    setDeviceData(prev => ({
      ...prev,
      article: value
    }));
  }

  const onChangeModel = (value) => {
    setDeviceData(prev => ({
      ...prev,
      model: value
    }));
  }

  const onChangeSavedAltitude = (value) => {
    setSavedAltitude(value);
  }

  const onCalculateAltitude = async () => {
    setLoading(true);
    try {
      await MechanismApi.calculateAltitude(mechanismId);
      await loadMechanismInfoUpdate();
      setCalibrationRefreshKey(key => key + 1);
      toaster.push(successMessage, {placement: "topEnd"});
    } catch (e) {
      console.error('Ошибка при расчете высоты:', e);
      toaster.push(apiErrorMessage(e), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const onSaveAltitude = async () => {
    // Допускаем и запятую, и точку: «2,25» и «2.25»
    const altitudeText = String(savedAltitude ?? '').trim().replace(',', '.');
    const numericAltitude = Number(altitudeText);

    if (altitudeText === '' || Number.isNaN(numericAltitude)) {
      toaster.push(
        <Message type="error" closable>Некорректное значение высоты</Message>,
        {placement: "topEnd"}
      );
      return;
    }

    setLoading(true);
    try {
      const result = await MechanismApi.saveAltitude(mechanismId, numericAltitude);
      if (result) {
        toaster.push(
          result.calibrated
            ? <Notification type={"success"} closable>
                <p>Высота сохранена, датчик давления откалиброван</p>
                <p>
                  Поправка: {Number(result.pressureOffset).toFixed(2).replace('.', ',')} мм рт. ст.
                  {result.history?.usedCount > 1 && <> (среднее по {result.history.usedCount} сохранениям)</>}
                </p>
              </Notification>
            : <Notification type={"warning"} closable>
                <p>Высота сохранена, но датчик не откалиброван</p>
                {result.calibrationError && <p>{result.calibrationError}</p>}
              </Notification>,
          {placement: "topEnd"}
        );
        await loadMechanismInfoUpdate();
        setCalibrationRefreshKey(key => key + 1);
      }
    } catch (e) {
      console.error('Ошибка при сохранении высоты:', e);
      toaster.push(apiErrorMessage(e), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const onManualMovement = async (mode) => {
    setLoading(true);
    try {
      await MechanismApi.manualMovement(mechanismId, mode);
      loadMechanismInfoUpdate().then()
      toaster.push(successMessage, {placement: "topEnd"});
    } catch (e) {
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const onSearchMechanism = async () => {
    setLoading(true);
    try {
      await MechanismApi.searchMechanism(mechanismId);
      loadMechanismInfoUpdate().then()
      toaster.push(successMessage, {placement: "topEnd"});
    } catch (e) {
      toaster.push(errorMessage, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  }

  const stopTimerSeconds = parseStopTimer(stopTimer);
  const isStopTimerValid = Number.isFinite(stopTimerSeconds);
  const isStopTimerEnabled = stopTimerSeconds > 0;

  const saveStopTimer = async (seconds) => {
    if (!isConnected || loading) return;
    setLoading(true);
    try {
      await MechanismApi.setStopTimer(mechanismId, seconds);
      await loadMechanismInfoUpdate();
      toaster.push(<Message type="success" closable>Таймер остановки сохранён</Message>, {placement: "topEnd"});
    } catch (error) {
      toaster.push(<Message type="error" closable>Не удалось сохранить таймер остановки</Message>, {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  const onSaveStopTimer = async () => {
    if (!isStopTimerValid) return;
    await saveStopTimer(stopTimerSeconds);
  };

  const onDisableStopTimer = async () => {
    if (!isStopTimerEnabled || loading || !isConnected) return;
    setStopTimer(formatStopTimer(0));
    setIsStopTimerRunning(false);
    await saveStopTimer(0);
  };

  const checkMechanismMovement = () => {
    return !movementPermission;
  }

  const getDisabledSectionStyle = (additionalStyles = {}) => {
    if (!isConnected) {
      return {...additionalStyles, ...OFFLINE_SECTION_STYLE};
    }

    return additionalStyles;
  }


  return (
    <Fragment>
      <div className="row mt-4" style={getDisabledSectionStyle()}>
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className="mechanism-header-controls mb-3">
            <Toggle
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={movementPermission}
              onChange={changeMovementPermission}
              loading={loading}
              disabled={loading || !isConnected}
            >
              <span>Выкл./Вкл. ход часового механизма</span>
            </Toggle>
            <Toggle
              color="blue"
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={isStopTimerEnabled}
              onChange={(checked) => {
                if (!checked) onDisableStopTimer();
              }}
              loading={loading && isStopTimerEnabled}
              disabled={!isStopTimerEnabled || loading || !isConnected}
            >
              <span>Таймер остановки</span>
            </Toggle>
          </div>
        </div>
      </div>

      <div className="row mb-3" style={getDisabledSectionStyle({
        opacity: checkMechanismMovement() ? 0.5 : 1
      })}>
        <div className="col-lg-9 col-md-12 col-sm-12 col-xs-12">
          <div className="mb-3">
            <RadioTileGroup
              className="w-100 mb-3"
              aria-label="Выкл./Вкл. автономный ход"
              value={autonomousEnabled ? 1 : 0}              // true ↔ 1 (первичный), false ↔ 0 (вторичный)
              onChange={(value) => {
                if (!isConnected) return;
                changeAutoMode(value === 1)
              }}
            >
              <div className="row">
                <div className="col-lg-6 col-md-12 mb-2">
                  {/* autoMode = true → value = 1 → выбран этот тайл */}
                  <RadioTile
                    className="h-100 d-flex align-items-start"
                    value={1}
                    label="Первичный механизм"
                  >
                    <span className="radio-tile-text">Работает как автономный механизм</span>
                  </RadioTile>
                </div>
                <div className="col-lg-6 col-md-12 mb-2">
                  {/* autoMode = false → value = 0 → выбран этот тайл */}
                  <RadioTile
                    className="h-100 d-flex align-items-start"
                    value={0}
                    label="Вторичный механизм"
                  >
        <span className="radio-tile-text">
          Работает от чередующихся импульсов часовой станции
        </span>
                  </RadioTile>
                </div>
              </div>
            </RadioTileGroup>

          </div>
        </div>
        <div className="col-lg-3 col-md-12 col-sm-12 col-xs-12 text-center align-items-center justify-content-center d-flex flex-column mb-2">
          <Button
            type='button'
            className="reload-btn"
            style={{marginRight: 30}}
            onClick={handleOpenRebootModal}
            loading={loading}
            disabled={loading || !isConnected}
          >
            Перезапустить устройство
          </Button>
          <div className="d-flex align-items-center">
            <Button
              className="mt-1 search-btn"
              type='button'
              onClick={onSearchMechanism}
              loading={loading}
              disabled={loading || disableFindButton() || !isConnected}
            >
              Поиск механизма
            </Button>
            <StatusControl status={adjustmentEnabled ? "online" : "offline"}/>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-1">
          <MechanismDataSection
            loading={loading}
            userTimezone={userTimezone}
            handleTimezoneChange={handleTimezoneChange}
            deviceTime={deviceTime}
            setDeviceTime={setDeviceTime}
            onSaveTime={onSaveTime}
            onSaveUTCTime={onSaveUTCTime}
            onSaveTimezone={onSaveTimezone}
            getTime={getTime}
            hourHandPosition={hourHandPosition}
            minuteHandPosition={minuteHandPosition}
            setHourHandPosition={setHourHandPosition}
            setMinuteHandPosition={setMinuteHandPosition}
            loadMechanismInfoUpdate={loadMechanismInfoUpdate}
            changeArrowsPosition={changeArrowsPosition}
            movementPermission={movementPermission}
            autonomousEnabled={autonomousEnabled}
            isDaylightSavingEnabled={isDaylightSavingEnabled}
            handleToggleDaylightSaving={handleToggleDaylightSaving}
            isMechanismMovement={checkMechanismMovement}
            isConnected={isConnected}/>
        </div>
        <div className="col-xl-6 col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-1">
          <MechanismInfoSection
            deviceData={deviceData}
            deviceId={mechanismId}
            deviceType={deviceType}
            onChangePlace={onChangePlace}
            onChangeArticle={onChangeArticle}
            onChangeModel={onChangeModel}
            onSavePlace={onSavePlace}
            onSaveArticle={onSaveArticle}
            onSaveModel={onSaveModel}
            onRefreshDeviceInfo={fetchAndSetData}
            loading={loading}
            isAdmin={isAdmin}
            isMechanismMovement={checkMechanismMovement}
            isConnected={isConnected}/>
        </div>
      </div>

      <div className="row mb-4" style={getDisabledSectionStyle({
        opacity: checkMechanismMovement() ? 0.5 : 1
      })}>
        <div className="col-12">
          <Button
            onClick={initialLoad}
            disabled={!isConnected}
          >
            Получить все параметры
          </Button>
        </div>
      </div>

      <RebootConfirmationModal
        show={showRebootModal}
        onHide={handleCloseRebootModal}
        onConfirm={handleConfirmReboot}
      />

      <div style={getDisabledSectionStyle()}>
        <p className="mb-2 mt-2">РУЧНОЙ ПОДГОН МЕХАНИЗМА</p>
        <div className="mechanism-adjustment">
          <div
            className="mechanism-adjustment__controls"
            style={{opacity: checkMechanismMovement() ? 0.5 : 1}}
          >
            <ButtonGroup className="mechanism-adjustment__buttons">
            <Whisper
              placement="top"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('запустить подгон назад')}>
              <IconButton
                appearance={movement === 'Backward' ? 'primary' : 'default'}
                color={movement === 'Backward' ? 'green' : undefined}
                onClick={() => onManualMovement(2)}
                icon={<FaAngleDoubleLeft/>}
              />
            </Whisper>
            <Whisper
              placement="top"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('остановить подгон')}>
              <IconButton
                appearance={movement === 'Stop' ? 'primary' : 'default'}
                color={movement === 'Stop' ? 'green' : undefined}
                onClick={() => onManualMovement(0)}
                icon={<FaStop/>}
              />
            </Whisper>
            <Whisper
              placement="top"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('запустить подгон вперёд')}>
              <IconButton
                appearance={movement === 'Forward' ? 'primary' : 'default'}
                color={movement === 'Forward' ? 'green' : undefined}
                onClick={() => onManualMovement(1)}
                icon={<FaAngleDoubleRight/>}/>
            </Whisper>
            </ButtonGroup>
            <InputGroup className="mechanism-adjustment__minutes">
              <InputGroup.Addon>Минуты подгона</InputGroup.Addon>
              <Input
                readOnly={true}
                size={"md"}
                value={adjustmentTime}
              />
            </InputGroup>
            {(adjustmentTime !== 0) && (
              <InputGroup className="mechanism-adjustment__remaining">
                <InputGroup.Addon>Время до окончания</InputGroup.Addon>
                <Input
                  readOnly={true}
                  size={"md"}
                  value={formatAdjustmentTime((adjustmentTime * impulse))}
                />
              </InputGroup>
            )}
            {adjustmentTime < 0 && <FaAngleDoubleLeft className="blinking-arrow"/>}
            {adjustmentTime > 0 && <FaAngleDoubleRight className="blinking-arrow"/>}
          </div>
          <InputGroup className={`mechanism-adjustment__stop-timer${isStopTimerRunning ? ' mechanism-adjustment__stop-timer--running' : ''}`}>
            <InputGroup.Addon>Таймер остановки</InputGroup.Addon>
            <Input
              aria-label="Таймер остановки, минуты и секунды"
              type="text"
              placeholder="мин:сек"
              title="Минуты и секунды (5:30) или число минут (5)"
              value={stopTimer}
              onChange={(value) => {
                setIsStopTimerRunning(false);
                setStopTimer(value);
              }}
              disabled={loading || !isConnected}
              aria-invalid={stopTimer !== '' && !isStopTimerValid}
            />
            <InputGroup.Button
              title="Сохранить таймер остановки"
              aria-label="Сохранить таймер остановки"
              onClick={onSaveStopTimer}
              loading={loading}
              disabled={loading || !isConnected || !isStopTimerValid}
            >
              <TfiSaveIcon/>
            </InputGroup.Button>
          </InputGroup>
        </div>
      </div>



      <MechanismMeteoDataSection
        isMechanismMovement={checkMechanismMovement}
        meteoData={meteoData}
        isConnected={isConnected}
      />

      <div className="row mb-4" style={getDisabledSectionStyle({
        opacity: checkMechanismMovement() ? 0.5 : 1
      })}>
        <div className="col-md-3 mt-1">
          <Button
            appearance="ghost"
            className="w-100"
            onClick={onCalculateAltitude}
            loading={loading}
            disabled={loading || !isConnected || checkMechanismMovement()}
          >
              Рассчитать авт. высоту
          </Button>
        </div>
        <div className="col-md-3 mt-1">
          <InputGroup>
            <InputGroup.Addon>Авт. высота (м)</InputGroup.Addon>
            <Input
              readOnly={true}
              size={"md"}
              value={currentAltitude}
            />
          </InputGroup>
        </div>
        <div className="col-md-3 mt-1">
          <InputGroup>
            <InputGroup.Addon>
              Руч. высота (м)
              <AltitudeCalibrationInfo
                mechanismId={mechanismId}
                refreshKey={calibrationRefreshKey}
                disabled={loading || !isConnected}
              />
            </InputGroup.Addon>
            <Input
              size={"md"}
              value={savedAltitude}
              onChange={onChangeSavedAltitude}
              disabled={!isConnected || checkMechanismMovement()}
            />
            <InputGroup.Button
              style={{cursor: 'pointer'}}
              title="Сохранить"
              onClick={onSaveAltitude}
              disabled={loading || !isConnected || checkMechanismMovement()}
            >
              <TfiSaveIcon/>
            </InputGroup.Button>
          </InputGroup>
        </div>
      </div>

      {isAdmin && (
        <MechanismPrivateDescriptionSection
          isMechanismMovement={checkMechanismMovement}
          isConnected={isConnected}
          privateDescription={deviceData?.privateDescription}
          mechanismId={mechanismId}/>
      )}

      <section style={getDisabledSectionStyle({
        opacity: checkMechanismMovement() ? 0.5 : 1
      })}>
        <div className="row">
          <div className="col-md-4 mt-1">
            <InputGroup>
              <InputGroup.Addon>Факт. ширина импульса</InputGroup.Addon>
              <Input
                size={"md"}
                value={impulse}
              />
            </InputGroup>
          </div>
          <div className="col-md-4 mt-1">
            <InputGroup>
              <InputGroup.Addon>Кол. импульсов для движения</InputGroup.Addon>
              <Input
                size={"md"}
                value={mechanism?.triggersCounter || ''}
              />
            </InputGroup>
          </div>
          <div className="col-md-4 mt-1">
            <InputGroup>
              <InputGroup.Addon>Счетчик секунд</InputGroup.Addon>
              <Input
                size={"md"}
                value={mechanism?.secondsCounter || ''}
              />
            </InputGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 mt-1">
            <InputGroup>
              <InputGroup.Addon>Кол. полных оборотов мин. стрелки</InputGroup.Addon>
              <Input
                size={"md"}
                value={mechanism?.minuteHandsFullTurnCounter || ''}
              />
            </InputGroup>
          </div>
          <div className="col-md-4 mt-1">
            <InputGroup>
              <InputGroup.Addon>Кол. полных оборотов час. стрелки</InputGroup.Addon>
              <Input
                size={"md"}
                value={mechanism?.hourHandsFullTurnCounter || ''}
              />
            </InputGroup>
          </div>
        </div>

      </section>

      {/* Корректировка стрелок, сбои и события из БД хранятся на сервере — доступны и когда устройство не в сети */}
      <div className="row">
        <div className="col-md-12">
          <HandsCorrection mechanismId={mechanismId} isAdmin={isAdmin}/>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <DeviceEvents deviceId={mechanismId} disabled={!isConnected}/>
        </div>
      </div>



    </Fragment>
  );
}

export default MechanismComponent;
